import { useState } from 'react'
import { MousePointer, PaintBucket, Palette, Pencil, UserPlus } from 'lucide-react'
import type { HashAlignment, Tool } from '@/types'
import type { SaveStatus } from '@/contexts/SaveStatusContext'
import { HashDialog } from '../toolbar/dialogs/HashDialog'
import { DrawOptionsDialog } from '../toolbar/dialogs/DrawOptionsDialog'
import { TooltipProvider } from '../ui/Tooltip'
import { ToolbarButton } from '../ui/ToolbarButton'
import { ToolbarFooter } from '../ui/ToolbarFooter'
import { EraserIcon } from '../toolbar/icons/EraserIcon'
import { HashIcon } from '../toolbar/icons/HashIcon'
import { ColorSwatchIndicator } from '../toolbar/ColorSwatchIndicator'
import { useDialogAutoClose } from '@/hooks'
import './concept-toolbar.css'

interface ConceptToolbarProps {
	selectedTool: Tool
	onToolChange: (tool: Tool) => void
	color: string
	onColorChange: (color: string) => void
	hashAlignment: HashAlignment
	onHashAlignmentChange: (alignment: HashAlignment) => void
	showColorPicker: boolean
	onShowColorPickerChange: (show: boolean) => void
	showDrawOptions: boolean
	onShowDrawOptionsChange: (show: boolean) => void
	lineStyle: 'solid' | 'dashed'
	lineEnd: 'none' | 'arrow' | 'tShape'
	brushSize: number
	pathMode: 'sharp' | 'curve'
	onLineStyleChange: (style: 'solid' | 'dashed') => void
	onLineEndChange: (end: 'none' | 'arrow' | 'tShape') => void
	onBrushSizeChange: (size: number) => void
	onPathModeChange: (mode: 'sharp' | 'curve') => void
	saveStatus?: SaveStatus
	showPanel?: boolean
	onTogglePanel?: () => void
}

export function ConceptToolbar({
	selectedTool,
	onToolChange,
	color,
	onColorChange: _onColorChange,
	hashAlignment,
	onHashAlignmentChange,
	showColorPicker,
	onShowColorPickerChange,
	showDrawOptions,
	onShowDrawOptionsChange,
	lineStyle,
	lineEnd,
	brushSize,
	pathMode,
	onLineStyleChange,
	onLineEndChange,
	onBrushSizeChange,
	onPathModeChange,
	saveStatus,
	showPanel,
	onTogglePanel,
}: ConceptToolbarProps) {
	const [showHashDialog, setShowHashDialog] = useState(false)

	// Auto-close DrawOptionsDialog when hovering out
	useDialogAutoClose({
		isOpen: showDrawOptions,
		onClose: () => onShowDrawOptionsChange(false),
		dataAttribute: 'data-draw-dialog',
	})

	return (
		<TooltipProvider>
			<div className="editor-toolbar concept-toolbar">
				{/* Select Tool */}
				<ToolbarButton
					icon={MousePointer}
					tooltip="Select (S)"
					isActive={selectedTool === 'select'}
					onClick={() => onToolChange('select')}
				/>

				{/* Add Player Tool */}
				<ToolbarButton
					icon={UserPlus}
					tooltip="Add Player (A)"
					isActive={selectedTool === 'addPlayer'}
					onClick={() => onToolChange('addPlayer')}
				/>

				{/* Draw Tool with Options Dialog */}
				<div className="toolbar-dialog-anchor">
					<ToolbarButton
						icon={Pencil}
						tooltip="Draw (D)"
						isActive={selectedTool === 'draw'}
						onClick={() => {
							if (selectedTool === 'draw') {
								onShowDrawOptionsChange(!showDrawOptions)
							} else {
								onToolChange('draw')
								onShowDrawOptionsChange(true)
							}
						}}
					/>

					{showDrawOptions && selectedTool === 'draw' && (
						<div className="concept-toolbar-dialog">
							<DrawOptionsDialog
								lineStyle={lineStyle}
								lineEnd={lineEnd}
								brushSize={brushSize}
								pathMode={pathMode}
								onLineStyleChange={onLineStyleChange}
								onLineEndChange={onLineEndChange}
								onBrushSizeChange={onBrushSizeChange}
								onPathModeChange={onPathModeChange}
								onClose={() => onShowDrawOptionsChange(false)}
								useRelativePosition={true}
							/>
						</div>
					)}
				</div>

				{/* Erase Tool */}
				<ToolbarButton
					icon={EraserIcon}
					tooltip="Erase (E)"
					isActive={selectedTool === 'erase'}
					onClick={() => onToolChange('erase')}
				/>

				{/* Color Tool - Custom with color swatch overlay */}
				<ToolbarButton
					icon={Palette}
					tooltip="Pick Color (C)"
					isActive={showColorPicker}
					onClick={() => onShowColorPickerChange(!showColorPicker)}
				>
					<ColorSwatchIndicator color={color} />
				</ToolbarButton>

				{/* Fill Tool */}
				<ToolbarButton
					icon={PaintBucket}
					iconStyle={{ transform: 'scaleX(-1)' }}
					tooltip="Fill (F)"
					isActive={selectedTool === 'fill'}
					onClick={() => onToolChange('fill')}
				/>

				<div className="toolbar-separator" />

			{/* Hash Marker Tool */}
				<div className="toolbar-dialog-anchor">
					<ToolbarButton
						icon={HashIcon}
						tooltip="Ball on Hash (H)"
						isActive={showHashDialog}
						onClick={() => setShowHashDialog(!showHashDialog)}
					/>

					{showHashDialog && (
						<div className="concept-toolbar-dialog">
							<HashDialog
								currentAlignment={hashAlignment}
								linemenAtDefault={true}
								onAlignmentChange={alignment => {
									onHashAlignmentChange(alignment)
									setShowHashDialog(false)
								}}
								onClose={() => setShowHashDialog(false)}
								useRelativePosition={true}
							/>
						</div>
					)}
				</div>

				{/* Page-mode extras: panel toggle + save status */}
				<ToolbarFooter
					showPanel={showPanel}
					onTogglePanel={onTogglePanel}
					saveStatus={saveStatus}
				/>
			</div>
		</TooltipProvider>
	)
}
